import { useParams } from 'react-router-dom'
import { buildErrorMessage, documentationRootGuid, projectFilesRootGuid } from '@constants'
import { AxiosError } from 'axios'
import {
  setDocumentAttachment,
  useCopyDocumentAttachment,
  useSetDocumentAttachment,
} from '@api/document-attachments/document-attachments'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { saveProjectFileItem } from '@mutations'

/**
 * Change the project file ("Dateien") attached to a markdown page. Every
 * mutation resolves with the updated markdown page, passed to
 * `onAttachmentChanged`.
 */
const useDocumentAttachmentMutations = (onAttachmentChanged: (file: FileItem) => void) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const handleError = (fallback: string) => (error: AxiosError) => {
    enqueueSnackbar(buildErrorMessage(error, fallback), { variant: 'error' })
  }

  const { mutate: mutateSetAttachment, isLoading: isSettingAttachment } =
    useSetDocumentAttachment<AxiosError>({
      mutation: {
        onSuccess: file => onAttachmentChanged(file as FileItem),
        onError: handleError('Fehler beim Entfernen der Datei'),
      },
    })

  const { mutateAsync: mutateCopyAttachment, isLoading: isCopyingAttachment } =
    useCopyDocumentAttachment<AxiosError>({
      mutation: {
        onSuccess: file => onAttachmentChanged(file as FileItem),
        onError: handleError('Fehler beim Kopieren der Datei'),
      },
    })

  // Upload the file to the project files and attach it
  const { mutate: uploadAttachment, isLoading: isUploadingAttachment } = useMutation({
    mutationFn: async ({ item, file }: { item: FileItem; file: File }) => {
      const projectFile = await saveProjectFileItem.request(projectId, projectFilesRootGuid, {
        guid: '',
        name: file.name.replace(/\.(pdf|docx?)$/i, ''),
        hierarchy_position: 0,
        parent_guid: '',
        type: 'file',
        // The file of an element's page stays one of the element's documents
        element_guid: item.element_guid,
        additional_data: item.additional_data?._elementData
          ? { _elementData: item.additional_data._elementData }
          : undefined,
        file,
      })

      return setDocumentAttachment(projectId as string, documentationRootGuid, item.guid, {
        attachment_guid: projectFile.guid,
      })
    },
    onSuccess: file => onAttachmentChanged(file as FileItem),
    onError: handleError('Fehler beim Hochladen der Datei'),
  })

  const removeAttachment = (item: FileItem) =>
    mutateSetAttachment({
      projectGuid: projectId as string,
      rootFolderGuid: documentationRootGuid,
      fileGuid: item.guid,
      data: { attachment_guid: null },
    })

  const copyAttachment = (item: FileItem) =>
    mutateCopyAttachment({
      projectGuid: projectId as string,
      rootFolderGuid: documentationRootGuid,
      fileGuid: item.guid,
    })

  return {
    uploadAttachment,
    removeAttachment,
    copyAttachment,
    isMutatingAttachment: isSettingAttachment || isCopyingAttachment || isUploadingAttachment,
  }
}

export default useDocumentAttachmentMutations
